/**
 * paragraphRange — "no selection means the paragraph". Walter 2026-08-22.
 *
 * The colour buttons marked the whole paragraph when nothing was selected,
 * while bold/italic/strike inserted an empty `****` pair — so the toolbar
 * disagreed with itself. Every button now reads the same way, and this is the
 * one helper that decides what "the paragraph" means for a bare caret.
 *
 * Two subtleties it has to get right, both of which would produce text that
 * looks bold-ish but renders as literal asterisks:
 *
 *  - LEADING LINE MARKERS are skipped. `# heading` must become
 *    `# **heading**`, never `**# heading**` — the marker has to stay first or
 *    the line stops being a heading at all. Same for `^`, `[x]`, `- [ ]`, a
 *    reminder bell and a log `@date`.
 *  - SURROUNDING WHITESPACE is excluded. Markdown needs the marker glued to a
 *    non-space character: `** text **` renders as literal asterisks.
 */

// Every one-sided leading marker in listicity's syntax, in the order they may
// stack (highlight can precede a header, per the editor's own peel loop).
const LEADING_MARKERS = /^(?:\^{1,3}\s?|#{1,6}\s+|\[x\]\s?|- \[[ xX]\]\s?|🔔\s?|\[DONE\]\s?|@\d{4}-\d{2}-\d{2}\s?)+/;

/**
 * paragraphIfNoSelection(content, selStart, selEnd) → [start, end]
 *
 * With a real selection, returns it untouched. With a bare caret, returns the
 * caret line's prose span — markers and outer whitespace excluded. An empty
 * line (or one that is nothing but markers) returns the caret unchanged, so
 * the caller falls back to inserting an empty pair.
 */
export function paragraphIfNoSelection(content, selStart, selEnd) {
  const v = content || '';
  const s = Math.max(0, Math.min(selStart, v.length));
  const e = Math.max(s, Math.min(selEnd, v.length));
  if (s !== e) return [s, e];

  const lineStart = v.lastIndexOf('\n', s - 1) + 1;
  let lineEnd = v.indexOf('\n', s);
  if (lineEnd === -1) lineEnd = v.length;
  const line = v.slice(lineStart, lineEnd);

  const marker = (line.match(LEADING_MARKERS) || [''])[0].length;
  const rest = line.slice(marker);
  const lead = rest.length - rest.trimStart().length;
  const trail = rest.length - rest.trimEnd().length;
  const from = lineStart + marker + lead;
  const to = lineEnd - trail;
  if (to <= from) return [s, e]; // nothing to wrap — caller inserts a pair
  return [from, to];
}
